import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ClipboardList,
  Eye,
  Pencil,
  Truck,
  RefreshCw,
  PauseCircle,
  PlayCircle,
  XCircle,
  AlertTriangle,
  Search,
  Package,
} from "lucide-react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import { useModules } from "../../hooks/useModules";
import {
  fetchOrders,
  refreshOrder,
  cancelOrder,
  setOrderAutoSync,
} from "../../store/slices/ordersSlice";
import { orderAPI } from "../../services/api";
import { Select } from "../../components/UI/FormField";
import { Pagination } from "../../components/ListView/Pagination";
import LoadingSpinner from "../../components/UI/LoadingSpinner";
import ActionMenu, {
  type ActionMenuItem,
} from "../../components/UI/ActionMenu";
import ConfirmationDialog from "../../components/modals/ConfirmationDialog";
import type { Order, OrderStatus } from "../../types/orders";

export const ORDER_STATUS_BADGE: Record<OrderStatus, string> = {
  new: "bg-gray-100 text-gray-700",
  shipment_created: "bg-blue-100 text-blue-800",
  in_transit: "bg-amber-100 text-amber-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-700",
};

export const orderTypeBadge = (type: Order["type"]) =>
  type === "sales_order"
    ? "bg-sky-100 text-sky-800"
    : "bg-purple-100 text-purple-800";

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("et-EE") : "—";

const fmtMoney = (v?: number | null, currency?: string | null) =>
  v == null
    ? "—"
    : `${v.toLocaleString("et-EE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency ? ` ${currency}` : ""}`;

interface PendingPrompt {
  kind: "refresh" | "cancel";
  order: Order;
  message: string;
}

const OrdersList: React.FC = () => {
  // "Create shipment" leaves this module for another one, so it is shown only
  // when the tenant has that other module too.
  const { has } = useModules();
  const { t } = useTranslation("orders");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { orders, loading, saving, pagination } = useAppSelector(
    (s) => s.orders,
  );

  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const searchDebounce = useRef<ReturnType<typeof setTimeout>>();
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [prompt, setPrompt] = useState<PendingPrompt | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Order | null>(null);

  useEffect(() => {
    dispatch(
      fetchOrders({
        type: type || undefined,
        status: status || undefined,
        search: debouncedSearch || undefined,
        page,
        limit: 20,
      }),
    );
  }, [dispatch, type, status, debouncedSearch, page]);

  const onSearchChange = (value: string) => {
    setSearch(value);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setPage(1);
      setDebouncedSearch(value.trim());
    }, 500);
  };

  const handleCreateShipment = async (order: Order) => {
    try {
      const res = await orderAPI.getShipmentPrefill(order._id);
      const { shipment: copyData, sourceOrder } = res.data.data;
      navigate("/shipments/new", { state: { copyData, sourceOrder } });
    } catch (e: any) {
      toast.error(e.response?.data?.message || t("toasts.prefillFailed"));
    }
  };

  const handleRefresh = async (order: Order, confirmed = false) => {
    try {
      const result = await dispatch(
        refreshOrder({ id: order._id, confirmed }),
      ).unwrap();
      toast.success(
        result?.removedFromDynamics
          ? t("toasts.refreshRemoved")
          : t("toasts.refreshed"),
      );
    } catch (e: any) {
      if (e?.code === "SHIPMENT_LINKED") {
        setPrompt({ kind: "refresh", order, message: e.message });
      } else {
        toast.error(e?.message || t("toasts.refreshFailed"));
      }
    }
  };

  const handleCancel = async (order: Order, confirmed = false) => {
    try {
      await dispatch(cancelOrder({ id: order._id, confirmed })).unwrap();
      toast.success(t("toasts.cancelled"));
    } catch (e: any) {
      if (e?.code === "SHIPMENT_LINKED") {
        setPrompt({ kind: "cancel", order, message: e.message });
      } else {
        toast.error(e?.message || t("toasts.cancelFailed"));
      }
    }
  };

  const handleAutoSync = async (order: Order) => {
    const enabled = !order.autoSyncEnabled;
    try {
      await dispatch(
        setOrderAutoSync({ id: order._id, enabled }),
      ).unwrap();
      toast.success(enabled ? t("toasts.autoSyncOn") : t("toasts.autoSyncOff"));
    } catch (e: any) {
      toast.error(typeof e === "string" ? e : t("toasts.saveFailed"));
    }
  };

  const rowMenu = (order: Order): ActionMenuItem[] => [
    {
      label: t("actions.view"),
      icon: <Eye className="w-4 h-4" />,
      onClick: () => navigate(`/orders/${order._id}`),
    },
    {
      label: t("actions.edit"),
      icon: <Pencil className="w-4 h-4" />,
      onClick: () => navigate(`/orders/${order._id}?edit=1`),
      hidden: order.status === "cancelled",
    },
    {
      label: t("actions.createShipment"),
      icon: <Truck className="w-4 h-4" />,
      onClick: () => handleCreateShipment(order),
      hidden:
        !has("shipments") ||
        !!order.linkedShipmentId ||
        order.status === "cancelled",
      className: "text-[#C97A3C] hover:bg-[#fdf5ec] font-semibold",
      divider: true,
    },
    {
      label: t("actions.refresh"),
      icon: <RefreshCw className="w-4 h-4" />,
      onClick: () => handleRefresh(order),
      divider: true,
      disabled: saving,
    },
    {
      label: order.autoSyncEnabled
        ? t("actions.disableAutoSync")
        : t("actions.enableAutoSync"),
      icon: order.autoSyncEnabled ? (
        <PauseCircle className="w-4 h-4" />
      ) : (
        <PlayCircle className="w-4 h-4" />
      ),
      onClick: () => handleAutoSync(order),
      hidden: order.status === "cancelled",
    },
    {
      label: t("actions.cancel"),
      icon: <XCircle className="w-4 h-4" />,
      onClick: () => setCancelTarget(order),
      variant: "danger",
      divider: true,
      hidden: order.status === "cancelled",
    },
  ];

  const linkedShipment = (order: Order) => {
    if (!order.linkedShipmentId) return <span className="text-[#8A9590]">—</span>;
    const id =
      typeof order.linkedShipmentId === "string"
        ? order.linkedShipmentId
        : order.linkedShipmentId._id;
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/shipments/${id}`);
        }}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-sky-50 text-sky-700 hover:bg-sky-100"
      >
        <Package className="w-3 h-3" />
        {order.linkedShipmentNumber}
      </button>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardList className="h-6 w-6 text-[#C97A3C]" />
          <div>
            <h1 className="text-xl font-medium text-[#0B0F0E]">
              {t("list.title")}
            </h1>
            <p className="text-[13px] text-[#8A9590]">{t("list.subtitle")}</p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="h-4 w-4 text-[#8A9590] absolute left-2.5 top-2.5" />
          <input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("list.searchPlaceholder")}
            className="h-9 w-64 pl-8 pr-3 text-[13px] border border-[rgba(11,15,14,0.15)] rounded-md focus:ring-2 focus:ring-[#C97A3C] focus:outline-none"
          />
        </div>
        <Select
          size="sm"
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            setPage(1);
          }}
          placeholder={t("list.allTypes")}
          options={[
            { value: "sales_order", label: t("list.typeSales") },
            { value: "purchase_order", label: t("list.typePurchase") },
          ]}
          className="max-w-[180px]"
        />
        <Select
          size="sm"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          placeholder={t("list.allStatuses")}
          options={[
            { value: "new", label: t("status.new") },
            { value: "shipment_created", label: t("status.shipment_created") },
            { value: "in_transit", label: t("status.in_transit") },
            { value: "delivered", label: t("status.delivered") },
            { value: "cancelled", label: t("status.cancelled") },
          ]}
          className="max-w-[180px]"
        />
      </div>

      <div className="bg-white rounded-md border border-[rgba(11,15,14,0.08)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-[#F5F4F0] text-[#8A9590]">
              <tr>
                <th className="text-left font-medium px-3 py-2">
                  {t("list.colNumber")}
                </th>
                <th className="text-left font-medium px-3 py-2">
                  {t("list.colType")}
                </th>
                <th className="text-left font-medium px-3 py-2">
                  {t("list.colPartner")}
                </th>
                <th className="text-left font-medium px-3 py-2">
                  {t("list.colOrderDate")}
                </th>
                <th className="text-left font-medium px-3 py-2">
                  {t("list.colDelivery")}
                </th>
                <th className="text-right font-medium px-3 py-2">
                  {t("list.colTotal")}
                </th>
                <th className="text-left font-medium px-3 py-2">
                  {t("list.colDynamicsStatus")}
                </th>
                <th className="text-left font-medium px-3 py-2">
                  {t("list.colStatus")}
                </th>
                <th className="text-left font-medium px-3 py-2">
                  {t("list.colShipment")}
                </th>
                <th className="w-10 px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="py-12">
                    <div className="flex justify-center">
                      <LoadingSpinner size="md" />
                    </div>
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td
                    colSpan={10}
                    className="px-3 py-8 text-center text-[#8A9590]"
                  >
                    {t("list.empty")}{" "}
                    <button
                      className="text-[#C97A3C] hover:underline"
                      onClick={() => navigate("/integrations")}
                    >
                      {t("list.noConnectionHint")}
                    </button>
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr
                    key={order._id}
                    onClick={() => navigate(`/orders/${order._id}`)}
                    className="border-t border-[rgba(11,15,14,0.06)] hover:bg-[#F8F8F6] cursor-pointer"
                  >
                    <td className="px-3 py-2 font-medium whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5">
                        {order.orderNumber}
                        {order.hasDiscrepancy && (
                          <AlertTriangle
                            className="w-3.5 h-3.5 text-amber-500"
                            aria-label="Discrepancy"
                          />
                        )}
                      </span>
                      {order.customerReference && (
                        <div className="text-[11px] text-[#8A9590]">
                          {order.customerReference}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded text-[11px] font-semibold ${orderTypeBadge(order.type)}`}
                      >
                        {order.type === "sales_order"
                          ? t("list.so")
                          : t("list.po")}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {(typeof order.partnerId === "object" &&
                        order.partnerId?.companyName) ||
                        order.partnerNameSnapshot ||
                        "—"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {fmtDate(order.orderDate)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {fmtDate(order.requestedDeliveryDate)}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {fmtMoney(order.totalValue, order.currency)}
                    </td>
                    <td className="px-3 py-2 text-[#8A9590]">
                      {order.dynamicsStatus || "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${ORDER_STATUS_BADGE[order.status]}`}
                      >
                        {t(`status.${order.status}`)}
                      </span>
                    </td>
                    <td className="px-3 py-2">{linkedShipment(order)}</td>
                    <td
                      className="px-2 py-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ActionMenu items={rowMenu(order)} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          pagination={{
            page: pagination.page,
            pages: pagination.pages,
            total: pagination.total,
            hasNext: pagination.page < pagination.pages,
            hasPrev: pagination.page > 1,
          }}
          onPageChange={setPage}
          pageSize={pagination.limit || 20}
        />
      </div>

      {/* Plain cancel confirmation (no linked shipment yet) */}
      <ConfirmationDialog
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={() => cancelTarget && handleCancel(cancelTarget)}
        title={t("prompts.cancelTitle")}
        message={t("prompts.cancelMessage", {
          number: cancelTarget?.orderNumber ?? "",
        })}
        confirmText={t("prompts.confirm")}
        type="danger"
      />

      {/* SHIPMENT_LINKED escalation prompt (server 409 message) */}
      <ConfirmationDialog
        isOpen={!!prompt}
        onClose={() => setPrompt(null)}
        onConfirm={() => {
          if (!prompt) return;
          if (prompt.kind === "refresh") handleRefresh(prompt.order, true);
          else handleCancel(prompt.order, true);
        }}
        title={
          prompt?.kind === "refresh"
            ? t("prompts.refreshLinkedTitle")
            : t("prompts.cancelLinkedTitle")
        }
        message={prompt?.message ?? ""}
        confirmText={t("prompts.confirm")}
        type={prompt?.kind === "cancel" ? "danger" : "warning"}
      />
    </div>
  );
};

export default OrdersList;
